import type { EventListQuery } from "@cdt/shared";
import { useState, type ReactNode } from "react";

import { EventVolumeChart } from "@/components/dashboard/EventVolumeChart";
import { DashboardErrorState } from "@/components/dashboard/DashboardErrorState";
import { DashboardSkeleton } from "@/components/dashboard/DashboardSkeleton";
import { ErrorsTable } from "@/components/dashboard/ErrorsTable";
import {
  KpiCards,
  PriorityPanel,
  RuntimeStatusPill,
} from "@/components/dashboard/KpiCards";
import { PipelineMetricsCard } from "@/components/dashboard/PipelineMetricsCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useDashboardMetrics } from "@/hooks/useDashboardMetrics";
import { useDashboardRetryEvent } from "@/hooks/useDashboardRetryEvent";
import { useDashboardStats } from "@/hooks/useDashboardStats";
import { useEventsQuery } from "@/hooks/useEventsQuery";

const ERROR_EVENTS_QUERY: EventListQuery = {
  status: ["ERROR"],
  search: undefined,
  dateFrom: undefined,
  dateTo: undefined,
  priority: undefined,
  priorityMin: undefined,
  categoryId: undefined,
  page: 1,
  pageSize: 20,
  sortBy: "createdAt",
  sortOrder: "desc",
};

export function DashboardPage() {
  const [days, setDays] = useState(30);
  const statsQuery = useDashboardStats();
  const metricsQuery = useDashboardMetrics(days);
  const errorEventsQuery = useEventsQuery(ERROR_EVENTS_QUERY);
  const retryMutation = useDashboardRetryEvent();

  const isInitialLoading =
    statsQuery.isLoading && statsQuery.data === undefined && metricsQuery.data === undefined;
  const hasFatalError =
    (statsQuery.isError && !statsQuery.data) || (metricsQuery.isError && !metricsQuery.data);

  if (isInitialLoading) {
    return (
      <div className="container space-y-6 py-8">
        <Header />
        <DashboardSkeleton />
      </div>
    );
  }

  if (hasFatalError) {
    const message =
      statsQuery.error instanceof Error
        ? statsQuery.error.message
        : metricsQuery.error instanceof Error
          ? metricsQuery.error.message
          : undefined;
    return (
      <div className="container space-y-6 py-8">
        <Header />
        <DashboardErrorState
          message={message}
          onRetry={() => {
            void statsQuery.refetch();
            void metricsQuery.refetch();
          }}
        />
      </div>
    );
  }

  const stats = statsQuery.data;

  return (
    <div className="container space-y-6 py-8">
      <Header pill={stats ? <RuntimeStatusPill health={stats.health} /> : null} />

      {stats ? <KpiCards stats={stats} /> : null}

      <div className="grid gap-6 xl:grid-cols-3">
        {metricsQuery.data ? (
          <div className="xl:col-span-2">
            <EventVolumeChart
              metrics={metricsQuery.data}
              days={days}
              onDaysChange={setDays}
              isFetching={metricsQuery.isFetching}
            />
          </div>
        ) : null}
        {stats ? (
          <PriorityPanel priorityStats={stats.priorityStats} />
        ) : null}
      </div>

      {stats ? <PipelineMetricsCard pipeline={stats.pipeline} /> : null}

      {errorEventsQuery.data && errorEventsQuery.data.items.length > 0 ? (
        <ErrorsTable
          items={errorEventsQuery.data.items}
          isFetching={errorEventsQuery.isFetching}
          retryingId={retryMutation.isPending ? (retryMutation.variables?.id ?? null) : null}
          onRetry={(id) => retryMutation.mutate({ id })}
        />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Errori con retry</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Nessun evento in errore al momento.
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Header({ pill }: { pill?: ReactNode }) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          Dashboard
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Monitoraggio operativo: KPI, volumi e coda errori con retry.
        </p>
      </div>
      {pill}
    </div>
  );
}
